import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { Utils, authJsonHeaders, handleSessionExpired, BILLING_CURRENCY_CODES, findOptionByValue } from '../../utils';
import Modal from '../common/Modal';

const formatBillingCycleLabel = (cycle) => (cycle === 'annual' ? 'Annual' : 'Monthly');

const EditablePricingSection = ({ rows, currency, onUpdatePrice }) => {
    const currencyLabel = findOptionByValue(BILLING_CURRENCY_CODES, currency)?.label;
    return (
    <div style={{ marginTop: '12px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
                <tr style={{ background: '#f3f4f6' }}>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Metric</th>
                    <th style={{ padding: '8px', textAlign: 'right', width: '140px' }}>Custom price</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((row) => (
                    <tr key={row.type} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '8px' }}>{row.type_label}</td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>
                            <input
                                type="number"
                                min="0"
                                step="any"
                                value={row.price}
                                onChange={(e) => onUpdatePrice(row.type, e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '6px 8px',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    textAlign: 'right',
                                }}
                            />
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
        <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#dc2626' }}>
            Currency #{currency}{currencyLabel ? ` (${currencyLabel})` : ''}. From user subscription — defaults from global plan_pricing. Change currency later via Manage pricing.
        </p>
    </div>
    );
};

const AdminUserPlanChangeModal = ({ requestId, userLabel, onClose, onSuccess }) => {
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [request, setRequest] = useState(null);
    const [editPricing, setEditPricing] = useState([]);
    const [billingCycle, setBillingCycle] = useState('monthly');

    useEffect(() => {
        if (!requestId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await fetch(`${Utils.baseurl}admin/plan-change-requests`, {
                    method: 'POST',
                    headers: authJsonHeaders(Utils.storageKeys.adminToken),
                    body: JSON.stringify({ requestId, page: 1, limit: 1, status: 'all' }),
                });
                const result = await res.json();
                if (cancelled) return;
                if (handleSessionExpired(res, result)) return;
                if (result.status && result.data?.[0]) {
                    const row = result.data[0];
                    setRequest(row);
                    setEditPricing((row.default_pricing || []).map((r) => ({ ...r })));
                    setBillingCycle(row.billing_cycle || 'monthly');
                }
            } catch (e) {
                console.error(e);
            }
            if (!cancelled) setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [requestId]);

    const updatePricingRow = (type, price) => {
        setEditPricing((prev) => prev.map((row) => (row.type === type ? { ...row, price } : row)));
    };

    const handleApprove = async () => {
        setActionLoading(true);
        try {
            const res = await fetch(`${Utils.baseurl}admin/plan-change-requests/approve`, {
                method: 'POST',
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
                body: JSON.stringify({
                    requestId,
                    billing_cycle: billingCycle,
                    custom_pricing: editPricing.map((row) => ({ type: row.type, price: row.price })),
                }),
            });
            const result = await res.json();
            if (handleSessionExpired(res, result)) return;
            if (result.status) {
                toast.success(result.message || 'Enterprise plan activated.');
                onSuccess?.();
                onClose();
            } else {
                toast.error(result.message || 'Failed to approve');
            }
        } catch (e) {
            console.error(e);
            toast.error('Error approving request');
        }
        setActionLoading(false);
    };

    const handleReject = async () => {
        if (!requestId) return;
        const { value: reason, isConfirmed } = await Swal.fire({
            title: 'Reject request?',
            input: 'textarea',
            inputLabel: 'Rejection reason',
            inputPlaceholder: 'Enter reason (required)',
            inputAttributes: { 'aria-label': 'Rejection reason' },
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Reject',
            cancelButtonText: 'Cancel',
            inputValidator: (value) => {
                if (!String(value || '').trim()) return 'Please enter a rejection reason';
            },
        });
        if (!isConfirmed) return;
        const trimmed = String(reason || '').trim();
        setActionLoading(true);
        try {
            const res = await fetch(`${Utils.baseurl}admin/plan-change-requests/reject`, {
                method: 'POST',
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
                body: JSON.stringify({ requestId, reason: trimmed }),
            });
            const result = await res.json();
            if (handleSessionExpired(res, result)) return;
            if (result.status) {
                toast.success('Request rejected');
                onSuccess?.();
                onClose();
            } else {
                toast.error(result.message || 'Failed to reject');
            }
        } catch (e) {
            console.error(e);
            toast.error('Error rejecting request');
        }
        setActionLoading(false);
    };

    const footer = !loading && request ? (
        <>
            <button
                type="button"
                onClick={handleReject}
                disabled={actionLoading}
                style={{ padding: '10px 18px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}
            >
                Reject
            </button>
            <button
                type="button"
                onClick={handleApprove}
                disabled={actionLoading}
                style={{ padding: '10px 18px', background: '#059669', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}
            >
                {actionLoading ? 'Processing...' : `Approve (${formatBillingCycleLabel(billingCycle)})`}
            </button>
        </>
    ) : null;

    return (
        <Modal
            title={`Approve request${userLabel ? ` — ${userLabel}` : ''}`}
            onClose={onClose}
            maxWidth="640px"
            footer={footer}
        >
            {loading ? (
                <p style={{ color: '#6b7280' }}>Loading request...</p>
            ) : !request ? (
                <p style={{ color: '#6b7280' }}>Request not found.</p>
            ) : (
                <>
                    {request.message ? (
                        <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                            <div style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '6px' }}>Message</div>
                            <div style={{ fontSize: '14px', color: '#374151' }}>{request.message}</div>
                        </div>
                    ) : null}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                        {['monthly', 'annual'].map((cycle) => (
                            <button
                                key={cycle}
                                type="button"
                                onClick={() => setBillingCycle(cycle)}
                                style={{
                                    padding: '8px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px',
                                    background: billingCycle === cycle ? '#4f46e5' : '#f3f4f6',
                                    color: billingCycle === cycle ? '#fff' : '#374151',
                                }}
                            >
                                {cycle === 'monthly' ? 'Monthly billing' : 'Annual billing'}
                            </button>
                        ))}
                    </div>
                    <EditablePricingSection
                        rows={editPricing}
                        currency={request.pricing_currency}
                        onUpdatePrice={updatePricingRow}
                    />
                </>
            )}
        </Modal>
    );
};

export default AdminUserPlanChangeModal;
